import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { Plus, Car, Pencil, Trash } from "@phosphor-icons/react";
import { useCollectionSchema, useReferenceData } from "../hooks/useCollectionSchema";
import DynamicForm from "../components/DynamicForm";

const VehiclesPage = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [formData, setFormData] = useState({});

    // Get dynamic schema
    const { fields, displayName, loading: schemaLoading } = useCollectionSchema("vehicles");
    const { references } = useReferenceData(fields);

    useEffect(() => {
        fetchVehicles();
    }, []);

    // Initialize form data when fields change
    useEffect(() => {
        if (fields.length > 0 && !editingVehicle) {
            initializeFormData();
        }
    }, [fields]);

    const initializeFormData = () => {
        const initial = {};
        fields.forEach(field => {
            initial[field.name] = field.default ?? "";
        });
        setFormData(initial);
    };

    const fetchVehicles = async () => {
        try {
            const response = await axios.get(`${API}/vehicles`);
            setVehicles(response.data);
        } catch (error) {
            toast.error("Error al cargar los vehículos");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        const missingRequired = fields
            .filter(f => f.required && !formData[f.name])
            .map(f => f.label || f.name);
        
        if (missingRequired.length > 0) {
            toast.error(`Campos requeridos: ${missingRequired.join(", ")}`);
            return;
        }

        try {
            if (editingVehicle) {
                await axios.put(`${API}/vehicles/${editingVehicle.id}`, formData);
                toast.success("Vehículo actualizado");
            } else {
                await axios.post(`${API}/vehicles`, formData);
                toast.success("Vehículo añadido");
            }
            fetchVehicles();
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al guardar");
        }
    };

    const handleEdit = (vehicle) => {
        setEditingVehicle(vehicle);
        // Copy all vehicle data to form
        const data = {};
        fields.forEach(field => {
            data[field.name] = vehicle[field.name] ?? field.default ?? "";
        });
        setFormData(data);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar este vehículo?")) return;
        try {
            await axios.delete(`${API}/vehicles/${id}`);
            toast.success("Vehículo eliminado");
            fetchVehicles();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const resetForm = () => {
        setEditingVehicle(null);
        initializeFormData();
    };

    // Get display value for a field
    const getDisplayValue = (field, value) => {
        if (value === undefined || value === null || value === "") return "-";
        
        switch (field.type) {
            case "reference":
                const refData = references[field.ref_collection] || [];
                const refItem = refData.find(r => r.id === value);
                return refItem?.name || refItem?.title || value;
            case "boolean":
                return value ? "Sí" : "No";
            case "date":
                return new Date(value).toLocaleDateString("es-ES");
            case "select":
                return value.charAt(0).toUpperCase() + value.slice(1).replace("-", " ");
            default:
                return value;
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusMap = {
            "disponible": "badge-success",
            "en-uso": "badge-warning",
            "mantenimiento": "badge-danger"
        };
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ") : "-";
        return <span className={statusMap[status] || "badge-info"}>{label}</span>;
    };

    if (loading || schemaLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    // Get table columns from schema (exclude text fields and arrays)
    const tableFields = fields.filter(f => f.type !== "text" && f.type !== "array");
    const availableCount = vehicles.filter(v => v.status === "disponible").length;

    return (
        <div className="space-y-6 animate-in" data-testid="vehicles-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">{displayName}</h1>
                    <p className="text-[#555555] mt-1">
                        {availableCount} de {vehicles.length} disponibles
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-vehicle-btn">
                            <Plus size={20} className="mr-2" />
                            Añadir vehículo
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingVehicle ? "Editar vehículo" : "Nuevo vehículo"}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="mt-4">
                            <DynamicForm 
                                fields={fields}
                                formData={formData}
                                setFormData={setFormData}
                                references={references}
                            />
                            <div className="flex gap-3 pt-6">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="btn-primary flex-1" data-testid="vehicle-save-btn">
                                    {editingVehicle ? "Guardar cambios" : "Añadir"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Vehicles Table */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-industrial">
                                {tableFields.map(field => (
                                    <TableHead key={field.name}>
                                        {field.label || field.name}
                                    </TableHead>
                                ))}
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {vehicles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tableFields.length + 1} className="text-center py-12">
                                        <Car size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                                        <p className="text-[#555555]">No hay vehículos registrados</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                vehicles.map((vehicle) => (
                                    <TableRow key={vehicle.id} className="table-industrial" data-testid={`vehicle-row-${vehicle.id}`}>
                                        {tableFields.map(field => (
                                            <TableCell key={field.name}>
                                                {field.name === "status" ? (
                                                    getStatusBadge(vehicle[field.name])
                                                ) : (
                                                    getDisplayValue(field, vehicle[field.name])
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(vehicle)}
                                                    className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                                                    data-testid={`edit-vehicle-${vehicle.id}`}
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(vehicle.id)}
                                                    className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                                                    data-testid={`delete-vehicle-${vehicle.id}`}
                                                >
                                                    <Trash size={18} />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default VehiclesPage;
